import { Spinner } from '@heroui/react';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError } from '../../api/client.js';
import { ActionButton } from './ActionButton.js';

/**
 * Loading, empty and error states.
 *
 * Every data view has three outcomes besides success, and each one deserves a
 * deliberate answer rather than a blank rectangle. Centralising them means a
 * new page gets all three by default instead of shipping with only the happy
 * path drawn.
 *
 * These states are also where the honesty rules land visibly: an empty result
 * says "nothing matched", never implies "nothing exists"; a failure says the
 * request failed, and does not leave stale data on screen looking current.
 */

// ---------------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------------

export function LoadingState({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div
      role="status"
      aria-live="polite"
      className="flex justify-center py-10"
    >
      <Spinner label={label ?? t('common.loading')} />
    </div>
  );
}

/**
 * A placeholder shaped like the content that is coming.
 *
 * Used where a spinner would cause the page to jump when the data lands.
 * `aria-hidden`, because a screen reader should hear the `LoadingState` status,
 * not a description of grey boxes.
 */
export function SkeletonRows({ rows = 5 }: { rows?: number }) {
  return (
    <div aria-hidden="true" className="flex flex-col gap-2">
      {Array.from({ length: rows }, (_, index) => (
        <div
          key={index}
          className="h-10 animate-pulse rounded-medium bg-default-100"
        />
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Empty
// ---------------------------------------------------------------------------

export interface EmptyStateProps {
  /** Already translated. */
  readonly title: string;
  readonly description?: string;
  readonly action?: ReactNode;
}

export function EmptyState({ title, description, action }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center gap-stack rounded-large border border-dashed border-default-200 px-gutter py-12 text-center">
      <p className="text-section font-medium text-default-700">{title}</p>
      {description ? (
        <p className="max-w-prose text-body text-default-600">{description}</p>
      ) : null}
      {action}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Error
// ---------------------------------------------------------------------------

/**
 * A failed request.
 *
 * The API returns a stable machine-readable CODE and never user-facing prose;
 * the sentence shown here comes from the `errors` namespace of the active
 * catalogue. That is what keeps the API locale-independent and reusable by a
 * future mobile client — and it means an unknown code degrades to a generic
 * translated message rather than leaking whatever English the server sent.
 *
 * The request id is never shown: it means nothing to the person reading the
 * message, and the server log already records it with the failure.
 */
export interface ErrorStateProps {
  readonly error: unknown;
  readonly onRetry?: () => void;
}

export function ErrorState({ error, onRetry }: ErrorStateProps) {
  const { t } = useTranslation();
  if (!error) return null;

  const code = error instanceof ApiError ? error.code : 'unknown';
  const message = t(`errors.${code}`, { defaultValue: t('errors.unknown') });

  return (
    <div
      role="alert"
      className="rounded-medium border border-danger-200 bg-danger-50 px-4 py-3 text-body text-danger-700"
    >
      <p>{message}</p>
      {onRetry ? (
        <div className="mt-3">
          <ActionButton priority="secondary" size="sm" onPress={onRetry}>
            {t('common.retry')}
          </ActionButton>
        </div>
      ) : null}
    </div>
  );
}
